/**
 * useAncestryTree.js
 *
 * Hook + helpers for the lineage (ancestry) view.
 *
 * Transforms the flat family-tree array from the API into two structures:
 *   1. ancestryRoot — a full binary tree of ancestors (used for hover-path highlighting)
 *   2. lineage      — a narrow linked list (one couple per row) that LineageView renders
 *
 * Also exports two small formatting helpers shared across LineageColumn,
 * PersonCard, and LineageView:
 *   - genLabel(depth)        — human-readable generation label ("Parents", "Great-Grandparents", …)
 *   - formatPersonName(data) — builds a display name from a person's data object
 */

import { useMemo } from "react";

/* ------------------------------------------------------------------ */
/*  Shared formatting helpers                                         */
/* ------------------------------------------------------------------ */

const GEN_LABELS = [
  "",             // 0 = target (no label)
  "Parents",
  "Grandparents",
  "Great-Grandparents",
];

/**
 * Returns a human-readable generation label for the given depth.
 * depth 1 → "Parents", depth 2 → "Grandparents", depth 4 → "Great-Great-Grandparents", etc.
 */
export function genLabel(depth) {
  if (depth < GEN_LABELS.length) return GEN_LABELS[depth];
  return `${"Great-".repeat(depth - 2)}Grandparents`;
}

/**
 * Builds a display name from a person's data object.
 * Handles missing fields gracefully — returns "Unknown" if everything is blank.
 */
export function formatPersonName(data) {
  return `${data?.first_name || ""} ${data?.last_name || ""} ${data?.numeric_suffix || ""}`.trim() || "Unknown";
}

/* ------------------------------------------------------------------ */
/*  Internal tree-building functions                                  */
/* ------------------------------------------------------------------ */

/**
 * Builds a Map of id → node for O(1) lookups.
 */
function buildNodeMap(tree) {
  const map = new Map();
  for (const node of tree) {
    map.set(String(node.id), node);
  }
  return map;
}

/**
 * Recursively walks up both parent chains (father + mother) to build
 * a nested ancestry tree. Uses a visited Set to handle C1-C2
 * intermarriage (same person reachable through both parents).
 *
 * Returns a node object:
 *   { id, data, father: <node|null>, mother: <node|null>, spouses: [...], duplicate: bool }
 */
function buildAncestry(nodeId, nodeMap, visited) {
  const id = String(nodeId);
  if (!nodeMap.has(id)) return null;

  const isDuplicate = visited.has(id);
  visited.add(id);

  const raw = nodeMap.get(id);
  const node = {
    id: raw.id,
    data: raw.data,
    father: null,
    mother: null,
    spouses: [],
    duplicate: isDuplicate,
  };

  // Don't recurse into a duplicate — prevents infinite loops
  if (isDuplicate) return node;

  // Collect spouse info (display only, not recursed into)
  if (raw.rels?.spouses) {
    for (const spouseId of raw.rels.spouses) {
      const spouse = nodeMap.get(String(spouseId));
      if (spouse) {
        node.spouses.push({
          id: spouse.id,
          data: spouse.data,
        });
      }
    }
  }

  // Walk up to father
  if (raw.rels?.father) {
    node.father = buildAncestry(raw.rels.father, nodeMap, visited);
  }

  // Walk up to mother
  if (raw.rels?.mother) {
    node.mother = buildAncestry(raw.rels.mother, nodeMap, visited);
  }

  return node;
}

/**
 * Pre-computes a map of ancestor ID → array of IDs forming the path
 * from that ancestor down to the target person.
 * Used for gold hover highlight.
 */
function buildPathMap(root) {
  const pathMap = new Map();

  function walk(node, pathFromRoot) {
    if (!node) return;

    const currentPath = [...pathFromRoot, String(node.id)];
    // Store the path from target up to this ancestor (reversed for display)
    pathMap.set(String(node.id), currentPath);

    if (!node.duplicate) {
      walk(node.father, currentPath);
      walk(node.mother, currentPath);
    }
  }

  walk(root, []);
  return pathMap;
}

/**
 * Compute the maximum depth of an ancestry subtree.
 */
function treeDepth(node, d) {
  if (!node || node.duplicate) return d;
  return Math.max(
    treeDepth(node.father, d + 1),
    treeDepth(node.mother, d + 1)
  );
}

/**
 * Build a linked-list-with-branches structure for the narrow lineage view.
 *
 * Each node in the list:
 *   { depth, primary, partner, extraSpouses, above, partnerAbove }
 *
 * - "primary" = the parent with deeper ancestry (father wins ties)
 * - "partner" = the other parent
 * - "above"   = primary's ascending lineage (linked list continues)
 * - "partnerAbove" = partner's ascending lineage (only when partner has parents)
 *
 * @param {object}  ancestryNode - current node in the binary ancestry tree
 * @param {number}  depth        - generation depth (1 = parents, 2 = grandparents, …)
 * @param {boolean} allowBranch  - whether this column is allowed to spawn a partner
 *   sub-branch. The main column passes true; partner sub-branches pass false so
 *   they can't cascade into an ever-widening fan in families with lots of intermarriage.
 */
function buildLineage(ancestryNode, depth, allowBranch) {
  if (!ancestryNode) return null;

  const father = ancestryNode.father;
  const mother = ancestryNode.mother;

  if (!father && !mother) return null;

  // Pick the parent with deeper ancestry as "primary" (father wins ties)
  const fatherDepth = treeDepth(father, 0);
  const motherDepth = treeDepth(mother, 0);

  let primary, partner;
  if (!mother) {
    primary = father;
    partner = null;
  } else if (!father) {
    primary = mother;
    partner = null;
  } else if (motherDepth > fatherDepth) {
    primary = mother;
    partner = father;
  } else {
    primary = father;
    partner = mother;
  }

  // Extra spouses: any spouses on the primary that are NOT the partner
  const partnerIdStr = partner ? String(partner.id) : null;
  const extraSpouses = (primary.spouses || []).filter(
    (sp) => String(sp.id) !== partnerIdStr
  );

  // Primary's line continues upward (inherits same branching permission)
  const above = primary.duplicate ? null : buildLineage(primary, depth + 1, allowBranch);

  // Partner's line branches IF allowed, they have parents, and aren't a duplicate.
  // Partner branches themselves are NOT allowed to branch further — keeps the
  // layout narrow instead of cascading into an ever-widening fan.
  let partnerAbove = null;
  if (allowBranch && partner && !partner.duplicate && (partner.father || partner.mother)) {
    partnerAbove = buildLineage(partner, depth + 1, false);
  }

  return {
    depth,
    primary,
    partner,
    extraSpouses,
    above,
    partnerAbove,
  };
}

/**
 * Hook: given the flat family tree array and a target person ID,
 * returns the ancestry tree, generation rows, path map, and lineage structure.
 *
 * @param {Array} tree - the flat family tree array from the API
 * @param {string|number|null} targetId - the person to show ancestry for
 * @returns {{ ancestryRoot, pathMap, maxGeneration, lineage }}
 */
export default function useAncestryTree(tree, targetId) {
  return useMemo(() => {
    if (!tree || !targetId) {
      return { ancestryRoot: null, pathMap: new Map(), maxGeneration: 0, lineage: null };
    }

    const nodeMap = buildNodeMap(tree);
    const visited = new Set();
    const ancestryRoot = buildAncestry(targetId, nodeMap, visited);
    const pathMap = buildPathMap(ancestryRoot);
    const maxGeneration = treeDepth(ancestryRoot, 0);

    // Build the narrow lineage linked list starting from the target person.
    // allowBranch=true — partner branches show their own ancestry with correct
    // generation labels relative to the target person (not resetting to "Parents").
    const lineage = buildLineage(ancestryRoot, 1, true);

    return {
      ancestryRoot,
      pathMap,
      maxGeneration,
      lineage,
    };
  }, [tree, targetId]);
}
